import { PropsWithChildren, useEffect, useRef } from 'react';
import { CircleAlert, CircleCheck, CircleX } from 'lucide-react';
import { animate } from 'motion';
import { AnimatePresence, motion, useMotionValue } from 'motion/react';

import { Spinner } from '@components/primitives/Spinner';
import { easeOutSmooth } from '@components/primitives/utilities';

import css from './styles.module.css';

function getMessageKey(children: React.ReactNode) {
  if (typeof children === 'string') {
    return children;
  } else if (typeof children === 'number') {
    return children.toString();
  } else {
    return JSON.stringify(children);
  }
}

export interface ToastProps extends PropsWithChildren {
  variant?: 'info' | 'success' | 'warning' | 'error' | 'loading';
}
export function Toast({ children, variant = 'info' }: ToastProps) {
  const messageKey = getMessageKey(children);
  const messageRef = useRef<HTMLDivElement>(null);
  const messageWidth = useMotionValue<number | 'auto'>('auto');

  useEffect(() => {
    const message = messageRef.current;
    if (!message) return;

    function update() {
      if (!message) return;
      const width = message.offsetWidth;
      animate([
        [
          messageWidth,
          width,
          {
            type: 'spring',
            bounce: 0.2,
            visualDuration: 0.2,
            onComplete: () => {
              messageWidth.set('auto');
            },
          },
        ],
      ]);
    }

    update();
    const observer = new ResizeObserver(update);
    observer.observe(message);

    return () => observer.disconnect();
  }, [messageKey]);

  return (
    <div className={css.toast} data-variant={variant}>
      <AnimatePresence initial={false}>
        {variant !== 'info' && (
          <motion.div
            className={css.iconContainer}
            aria-hidden
            initial={{ opacity: 0, x: '0em', width: '0em' }}
            animate={{ opacity: 1, x: '-0.25em', width: '1.25em' }}
            exit={{ opacity: 0, x: '0em', width: '0em' }}
            transition={{ duration: 0.3, ease: easeOutSmooth }}
          >
            <AnimatePresence initial={false} mode="popLayout">
              {variant === 'success' && (
                <motion.div
                  key="success"
                  className={css.icon}
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.5 }}
                  transition={{
                    type: 'spring',
                    bounce: 0.4,
                    visualDuration: 0.2,
                  }}
                >
                  <CircleCheck className={css.successIcon} />
                </motion.div>
              )}

              {variant === 'warning' && (
                <motion.div
                  key="warning"
                  className={css.icon}
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.5 }}
                  transition={{
                    type: 'spring',
                    bounce: 0.4,
                    visualDuration: 0.2,
                  }}
                >
                  <CircleAlert className={css.warningIcon} />
                </motion.div>
              )}

              {variant === 'error' && (
                <motion.div
                  key="error"
                  className={css.icon}
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.5 }}
                  transition={{
                    type: 'spring',
                    bounce: 0.4,
                    visualDuration: 0.2,
                  }}
                >
                  <CircleX className={css.errorIcon} />
                </motion.div>
              )}

              {variant === 'loading' && (
                <motion.div
                  key="loading"
                  className={css.icon}
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0.99, scale: 1 }}
                  transition={{ duration: 0.2, ease: easeOutSmooth }}
                >
                  <Spinner className={css.loadingIcon} />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        className={css.content}
        style={{ width: messageWidth } as any}
      >
        <AnimatePresence initial={false} mode="popLayout">
          <motion.div
            key={messageKey}
            className={css.message}
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            exit={{
              opacity: 0,
              filter: 'blur(8px)',
            }}
            transition={{ duration: 0.2, ease: easeOutSmooth }}
          >
            <div ref={messageRef}>{children}</div>
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
